<style lang="scss" src='./SensorThreshold.scss' scoped></style>
<template src="./SensorThreshold.html"></template>

<script>
  import {
    inRange,
    union,
    max,
    min,
  } from 'lodash';
  import VueSlider from 'vue-slider-component';
  import 'vue-slider-component/theme/antd.css';
  import {
    getSensors,
    updateThreshold,
    getZones,
    getThresholdPresets,
    updateSingleThreshold,
  } from '@/api/index';
  import SecurityComponent from '@/components/SecurityComponent.vue';
  import CustomTable from '../CustomTable.vue';

  export default {
    name: 'SensorThreshold',
    extends: SecurityComponent,
    components: {
      VueSlider,
      CustomTable,
    },
    props: {
      display: {
        type: Boolean,
        required: false,
      },
    },
    data() {
      return {
        sensors: [],
        filteredSensors: [],
        thresholds: this.G_getThresholds(),
        zones: [],
        ranges: [{
            label: '1 - 9',
            range: [1, 9],
            selected: false,
          },
          {
            label: '10 - 18',
            range: [10, 18],
            selected: false,
          },/*
          {
            label: '19 - 27',
            range: [19, 27],
            selected: false,
          },*/
        ],
        presets: [],
        presetSelected: null,
        defaultThreshold: this.G_getDefaultThreshold(),
        loading: {
          sensors: false,
        },
        isGolfCourse: false,
        currentSort: 'location_code',
        currentSortOrder: 'desc',
      };
    },
    computed: {
      sensorsSelected: function sensorsSelectedFn() {
        return this.filteredSensors.filter((s) => s.selected).length;
      },
    },
    methods: {

      saveThresholds() {
        const sensorsSelected = this.sensors.filter((s) => s.selected);
        const sensorThresholds = sensorsSelected.map((s) => {
          const sensorThreshold = s.threshold;
          this.thresholds.forEach((t) => {
            sensorThreshold[`min_${t.key}`] = this.G_getDefaultMeasurement({
              val: t.value[0],
              uom: t.uom
            });
            sensorThreshold[`max_${t.key}`] = this.G_getDefaultMeasurement({
              val: t.value[1],
              uom: t.uom
            });
          });

          delete sensorThreshold.min_salinity_ms;
          delete sensorThreshold.max_salinity_ms;
          delete sensorThreshold.min_et_rate;
          delete sensorThreshold.max_et_rate;
          return sensorThreshold;
        });

        updateThreshold(sensorThresholds)
          .then(() => {
            if (process.env.VUE_APP_SENTRY_ENV == "production") {
              this.$analytics.setCurrentScreen("Web Alerts Sensor Setup Save");
              this.$analytics.logEvent("web_alerts_sensor_setup_save");
            }

            this.presetSelected = null;
            this.$notify({
              text: 'Thresholds updated',
              type: 'success',
            });
          });
      },

      reset() {
        this.presetSelected = null;
        this.zones.forEach((z) => {
          z.selected = false;
        });
        this.ranges.forEach((r) => {
          r.selected = false;
        });
        this.loadSensors(true);
      },

      setPreset() {
        if (this.presetSelected) {
          this.setThresholdValues(this.presetSelected);
        }
      },

      setThresholdValues(threshold) {
        this.thresholds.forEach((t) => {
          const minVal = this.G_getMeasurement({
            val: threshold[`min_${t.key}`],
            uom: t.uom
          });
          const maxVal = this.G_getMeasurement({
            val: threshold[`max_${t.key}`],
            uom: t.uom
          });
          t.value = [minVal, maxVal];
        });
      },

      selectAllFilteredSensors(selected) {
        this.filteredSensors.forEach((s) => {
          s.selected = selected;
        });
      },

      selectAllSensors(selected) {
        this.filteredSensors.forEach((s) => {
          s.selected = selected;
        });
      },
      filterSensors(zoneIds, ranges) {
        this.selectAllSensors(false);
        if (zoneIds.length || ranges.length) {
          this.filteredSensors = this.sensors.filter((s) => (zoneIds.length ? zoneIds.includes(s.zone_id) : true) && (ranges
            .length ? (s.hole && inRange(s.hole.hole_number, min(ranges), max(ranges) + 1)) : true));
        } else {
          this.filteredSensors = this.sensors;
        }
      },
      
      onSelect(sensors) {
        if (sensors && sensors.length && !this.presetSelected) {
          this.setThresholdValues(sensors[0].threshold);
        }
      },

      customSensorSort(value) {

        if (this.currentSort != 'location_code') {
          return value.sort((a, b) => {
            const sortBy = this.currentSort;
            if (!a[sortBy] && !b[sortBy]) {
              return 0;
            }
            else if (a[sortBy] && !b[sortBy]) {
              return -1;
            } 
            else if (!a[sortBy] && b[sortBy]) {
              return 1;
            } 
            else {
              if (this.currentSortOrder === "desc") {
                return String(a[sortBy]).localeCompare(String(b[sortBy]));
              }
              return String(b[sortBy]).localeCompare(String(a[sortBy]));
            }
            return -1;
          });
        } else {
          return value.sort((a, b) => {
            const sortBy = this.currentSort;
            if (!a['hole_id'] && !b['hole_id']) {
              // no hole, order by location_code
              if (!a[sortBy] && !b[sortBy]) {
                return 0;
              }
              else if (a[sortBy] && !b[sortBy]) {
                return -1;
              } 
              else if (!a[sortBy] && b[sortBy]) {
                return 1;
              } 
              else {
                if (this.currentSortOrder === "desc") {
                    return String(a[sortBy]).localeCompare(String(b[sortBy]));
                }
                return String(b[sortBy]).localeCompare(String(a[sortBy]));
              }
            }
            else if (a['hole_id'] && !b['hole_id']) {
              return -1;
            } 
            else if (!a['hole_id'] && b['hole_id']) {
              return 1;
            } 
            else {
              const holeA = parseInt(a['hole']['hole_number']);
              const holeB = parseInt(b['hole']['hole_number']);

              if (holeA > holeB){
                if (this.currentSortOrder === "desc") {
                  return 1;
                }
                return -1;
              } else if (holeB > holeA) {
                if (this.currentSortOrder === "desc") {
                  return -1;
                }
                return 1;
              } else {
                if (this.currentSortOrder === "desc") {
                  return String(a[sortBy]).localeCompare(String(b[sortBy]));
                }
                return String(b[sortBy]).localeCompare(String(a[sortBy]));
              }
            }
            return -1;
          });
        }
      },

      loadSensors(showLoading) {
        const self = this;
        this.loading.sensors = showLoading;
        getSensors(null).then((res) => {
          const sensorsAux = res.sensors.filter((item) => (
            [self.consts.SensorStatus.DEPLOYED, self.consts.SensorStatus.CHECK].includes(item.status_id)));

          let sensorList = sensorsAux.map((sensor) => {
            if (!sensor.threshold) {
              //  Set default threshold if has not one
              sensor.threshold = {
                ...this.defaultThreshold,
                sensor_id: sensor.sensor_id
              };
            }
            sensor.selected = false;
            return sensor;
          });

          this.sensors = this.customSensorSort(sensorList);
          this.filteredSensors = this.sensors;
          this.loading.sensors = false;
        });
      },

      loadZones() {
        getZones().then((res) => {

          let zones = res.zones.filter(x => !x.hidden);
          
          this.zones = zones.map((zone) => ({
            ...zone,
            selected: false,
          }));
        });
      },

      loadPresets() {
        getThresholdPresets().then((res) => {
          this.presets = res.presets;
        });
      },

      loadData() {
        this.loadSensors(true);
        this.loadZones();
        this.loadPresets();
      },

      getInterval(measure) {
        switch(measure.key){
          case 'salinity': return 0.1;
          case 'moisture': return 0.5;
          default: return 1;
        }
      },

      saveSingleThresholds(measure) {
        let params = {}
        const sensorsSelected = this.sensors.filter((s) => s.selected);
        const sensorThresholds = sensorsSelected.map((s) => {
          let sensorThreshold = null;
          this.thresholds.forEach((t) => {
            if(t.key == measure.key){
              sensorThreshold = {
                active: s.threshold['active'],
                sensor_id: s.threshold['sensor_id'],
                threshold_id: s.threshold['threshold_id'],
              }
              params[`min_${t.key}`] = this.G_getDefaultMeasurement({
                val: t.value[0],
                uom: t.uom
              });
              params[`max_${t.key}`] = this.G_getDefaultMeasurement({
                val: t.value[1],
                uom: t.uom
              });
            }
          });

          return sensorThreshold['threshold_id'];
        });

        if(params && sensorThresholds){
          let body = [{
            thresholds: sensorThresholds,
            parameters: params,
          }]
          updateSingleThreshold(body)
            .then(() => {
              if (process.env.VUE_APP_SENTRY_ENV == "production") {
                this.$analytics.setCurrentScreen("Web Alerts Sensor Setup Save Single");
                this.$analytics.logEvent("web_alerts_sensor_setup_save_single");
              }
  
              this.presetSelected = null;
              this.$notify({
                text: 'Thresholds updated',
                type: 'success',
              });
            });
        }
      },
    },

    mounted() {
      if (process.env.VUE_APP_SENTRY_ENV == "production") {
        this.$analytics.setCurrentScreen("Web Alerts Sensor Setup");
        this.$analytics.logEvent("web_alerts_sensor_setup");
      }
      this.isGolfCourse = 
        ( (this.$store.state.course.facility_type && this.$store.state.course.facility_type.is_golf_course) || !this.$store.state.course.facility_type) 
          ? true : false;
          
      this.loadData();
    },
  };

</script>
